import { useCallback, useMemo, useRef, useState } from "react";
import * as Crypto from "expo-crypto";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useRouter } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PressableScale } from "pressto";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { View, Text, Card } from "@src/components";
import { Icon } from "@src/components/Icon";
import { LegacySnapshotImage } from "@src/features/cameras/components/LegacySnapshotImage";
import {
  CreateSiteWideContactBottomSheet,
  type CreateSiteWideContactData,
} from "@src/features/alarms/components/CreateSiteWideContactBottomSheet";
import {
  ManageContactBottomSheet,
  type ManageContactResult,
} from "@src/features/alarms/components/ManageContactBottomSheet";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useAlarmsByCameras, useUpdateCameraContacts } from "@src/services/api/hooks";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useTenantId } from "@src/features/tenants";
import { showSuccessToast, showErrorToast, showInfoToast } from "@src/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import type { CameraApiResponse } from "@src/features/cameras";
import type { AlarmContacts } from "@visioni/a3s-alarm-schedules/schema";
import { mergeApiContacts } from "@src/features/contacts/stores/contacts-store";
import Animated, { FadeOut, FadeIn, LinearTransition } from "react-native-reanimated";

export type SiteWideContact = {
  id: string;
  name: string;
  methods: string[];
  isVerified: boolean;
  email?: string;
  phoneNumber?: string;
};

type CameraContactSummary = {
  camera: CameraApiResponse;
  cameraKey: string;
  methods: string[];
};

/** Return only contacts that are present on every camera on the site. */
function aggregateSiteWideContacts(contactsByCamera: (AlarmContacts | null | undefined)[]): SiteWideContact[] {
  const cameraCount = contactsByCamera.filter(Boolean).length;
  if (cameraCount === 0) return [];

  // Use mergeApiContacts per camera to get unified contacts, then count presence across cameras
  const emailPresence = new Map<string, number>();
  const phonePresence = new Map<string, number>();

  for (const contacts of contactsByCamera) {
    if (!contacts) continue;
    const seen = new Set<string>();
    for (const ec of contacts.email_contacts) {
      const key = ec.email.toLowerCase();
      if (!seen.has(`e:${key}`)) {
        seen.add(`e:${key}`);
        emailPresence.set(key, (emailPresence.get(key) ?? 0) + 1);
      }
    }
    for (const sc of contacts.sms_contacts) {
      if (!seen.has(`s:${sc.phone_number}`)) {
        seen.add(`s:${sc.phone_number}`);
        phonePresence.set(sc.phone_number, (phonePresence.get(sc.phone_number) ?? 0) + 1);
      }
    }
  }

  // Merge all cameras' contacts into unified contacts using the store's merge logic
  const merged: AlarmContacts = { email_contacts: [], sms_contacts: [] };
  const seenEmails = new Set<string>();
  const seenPhones = new Set<string>();

  for (const contacts of contactsByCamera) {
    if (!contacts) continue;
    for (const ec of contacts.email_contacts) {
      const key = ec.email.toLowerCase();
      if (!seenEmails.has(key) && (emailPresence.get(key) ?? 0) >= cameraCount) {
        seenEmails.add(key);
        merged.email_contacts.push(ec);
      }
    }
    for (const sc of contacts.sms_contacts) {
      if (!seenPhones.has(sc.phone_number) && (phonePresence.get(sc.phone_number) ?? 0) >= cameraCount) {
        seenPhones.add(sc.phone_number);
        merged.sms_contacts.push(sc);
      }
    }
  }

  // Use the shared merge logic to unify email + SMS by name
  const unified = mergeApiContacts(merged);

  return unified.map((c) => ({
    id: c.id,
    name: c.name,
    methods: [c.email ? "Email" : null, c.phoneNumber ? "SMS" : null].filter(Boolean) as string[],
    isVerified: !!c.email,
    email: c.email,
    phoneNumber: c.phoneNumber,
  }));
}

/** Summarise notification methods for a camera. */
function getCameraContactMethods(contacts: AlarmContacts | null | undefined): string[] {
  if (!contacts) return [];
  const methods: string[] = [];
  if (contacts.email_contacts.length > 0) methods.push("Email");
  if (contacts.sms_contacts.length > 0) methods.push("SMS");
  return methods;
}

export default function ManageNotificationsScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { siteCameras, cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const { headerLeft } = useHeaderBackButton();
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const createContactRef = useRef<BottomSheetModal>(null);
  const manageContactRef = useRef<BottomSheetModal>(null);
  const [selectedContact, setSelectedContact] = useState<SiteWideContact | null>(null);
  const updateCameraContacts = useUpdateCameraContacts();

  const effectiveSiteId = selectedSiteId ?? "";
  const alarmQueries = useAlarmsByCameras(effectiveSiteId, cameraKeys);

  const isLoading = alarmQueries.some((q) => q.isLoading);

  const contactsByCamera = useMemo(() => alarmQueries.map((q) => q.data?.settings?.contacts ?? null), [alarmQueries]);

  const siteWideContacts = useMemo(() => aggregateSiteWideContacts(contactsByCamera), [contactsByCamera]);

  const cameraContactSummaries = useMemo<CameraContactSummary[]>(
    () =>
      siteCameras.map((camera, index) => ({
        camera,
        cameraKey: cameraKeys[index],
        methods: getCameraContactMethods(contactsByCamera[index]),
      })),
    [siteCameras, cameraKeys, contactsByCamera],
  );

  const handleContactPress = useCallback((contact: SiteWideContact) => {
    setSelectedContact(contact);
    manageContactRef.current?.present();
  }, []);

  const handleManageContactSave = useCallback(
    async (contact: SiteWideContact, result: ManageContactResult) => {
      if (!selectedSiteId) return;

      try {
        await Promise.all(
          cameraKeys.map((cameraKey, index) => {
            const existing = contactsByCamera[index];
            const nextContacts: AlarmContacts = {
              email_contacts: [...(existing?.email_contacts ?? [])],
              sms_contacts: [...(existing?.sms_contacts ?? [])],
            };

            // Update email: remove existing then re-add if enabled
            if (contact.email) {
              nextContacts.email_contacts = nextContacts.email_contacts.filter(
                (e) => e.email.toLowerCase() !== contact.email!.toLowerCase(),
              );
              if (result.emailEnabled) {
                nextContacts.email_contacts.push({ email: contact.email, name: contact.name });
              }
            }

            // Update SMS: remove existing then re-add if enabled
            if (contact.phoneNumber) {
              nextContacts.sms_contacts = nextContacts.sms_contacts.filter(
                (s) => s.phone_number !== contact.phoneNumber,
              );
            }
            if (result.smsEnabled && result.phoneNumber) {
              nextContacts.sms_contacts.push({ phone_number: result.phoneNumber, name: contact.name });
            }

            return updateCameraContacts.mutateAsync({
              siteId: selectedSiteId,
              cameraKey,
              contacts: nextContacts,
            });
          }),
        );
        manageContactRef.current?.dismiss();
        setTimeout(() => {
          showSuccessToast({ message: "Contact updated" });
          queryClient.invalidateQueries({ queryKey: ["alarms", tenantId, selectedSiteId] });
        }, 300);
      } catch (error) {
        showErrorToast({ message: "Error", description: "Failed to update contact." });
        console.error("Failed to update site-wide contact:", error);
      }
    },
    [selectedSiteId, cameraKeys, contactsByCamera, updateCameraContacts, queryClient, tenantId],
  );

  const handleAddContact = useCallback(() => {
    createContactRef.current?.present();
  }, []);

  const handleSaveContact = useCallback(
    async (data: CreateSiteWideContactData) => {
      if (!selectedSiteId) return;

      // Check if contact already exists on all cameras
      const alreadyExists =
        (data.email && siteWideContacts.some((c) => c.email?.toLowerCase() === data.email!.toLowerCase())) ||
        (data.phoneNumber && siteWideContacts.some((c) => c.phoneNumber === data.phoneNumber));

      if (alreadyExists) {
        showInfoToast({ message: "Contact already exists" });
        return;
      }

      const fullName = [data.firstName, data.lastName].filter(Boolean).join(" ");
      const contactId = Crypto.randomUUID();

      try {
        await Promise.all(
          cameraKeys.map((cameraKey, index) => {
            const existing = contactsByCamera[index];
            const nextContacts: AlarmContacts = {
              email_contacts: [...(existing?.email_contacts ?? [])],
              sms_contacts: [...(existing?.sms_contacts ?? [])],
            };

            if (data.email) {
              const emailExists = nextContacts.email_contacts.some(
                (e) => e.email.toLowerCase() === data.email!.toLowerCase(),
              );
              if (!emailExists) {
                nextContacts.email_contacts.push({ id: contactId, email: data.email, name: fullName });
              }
            }
            if (data.phoneNumber) {
              const phoneExists = nextContacts.sms_contacts.some((s) => s.phone_number === data.phoneNumber);
              if (!phoneExists) {
                nextContacts.sms_contacts.push({ id: contactId, phone_number: data.phoneNumber, name: fullName });
              }
            }

            return updateCameraContacts.mutateAsync({
              siteId: selectedSiteId,
              cameraKey,
              contacts: nextContacts,
            });
          }),
        );

        createContactRef.current?.dismiss();
        setTimeout(() => {
          showSuccessToast({ message: "Contact added" });
          queryClient.invalidateQueries({ queryKey: ["alarms", tenantId, selectedSiteId] });
        }, 300);
      } catch (error) {
        showErrorToast({ message: "Error", description: "Failed to add contact to some cameras." });
        console.error("Failed to create site-wide contact:", error);
      }
    },
    [selectedSiteId, cameraKeys, contactsByCamera, siteWideContacts, updateCameraContacts, queryClient, tenantId],
  );

  const handleCameraPress = (cameraKey: string) => {
    if (selectedSiteId) {
      const title = encodeURIComponent(`${cameraKey} Contacts`);
      router.push(
        `/contacts/site-alarm?siteId=${encodeURIComponent(selectedSiteId)}&cameraKey=${encodeURIComponent(cameraKey)}&title=${title}`,
      );
    }
  };

  return (
    <>
      <Stack.Screen options={{ title: "Notifications", headerLeft }} />
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          paddingHorizontal: theme.spacing["3xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        {!isLoading && (
          <View>
            {/* Site-wide contacts */}
            <View>
              <Text textStyle="xs/regular" color="textMuted" mb="lg">
                Site-wide contacts
              </Text>
            </View>

            <View>
              {siteWideContacts.length > 0 ?
                <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <Card style={styles.card}>
                    {siteWideContacts.map((contact, index) => {
                      const isLast = index === siteWideContacts.length - 1;
                      return (
                        <PressableScale key={contact.id} onPress={() => handleContactPress(contact)}>
                          <View style={[styles.contactRow, isLast && { borderBottomWidth: 0 }]} px="5xl">
                            <View flex={1}>
                              <Text textStyle="sm/regular" color="text">
                                {contact.name}
                              </Text>
                            </View>
                            <View flexDirection="row" alignItems="center" gap="sm">
                              <Text textStyle="xs/regular" color="textMuted">
                                {contact.methods.join(", ")}
                              </Text>
                              {contact.isVerified && (
                                <View style={[styles.verifiedDot, { backgroundColor: theme.colors.success[500] }]} />
                              )}
                              <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
                            </View>
                          </View>
                        </PressableScale>
                      );
                    })}
                  </Card>
                </Animated.View>
              : <Animated.View entering={FadeIn} exiting={FadeOut}>
                  <Card py={theme.spacing["7xl"]} rounded={theme.spacing.md}>
                    <View alignItems="center">
                      <Icon
                        name="users"
                        size={48}
                        color={theme.colors.text}
                        style={{ marginBottom: theme.spacing["2xl"] }}
                      />
                      <Text textStyle="md/medium" fontWeight="600" mb="xs" textAlign="center">
                        No site-wide contacts found
                      </Text>
                      <Text color="text" textStyle="sm/regular" textAlign="center" px="xl">
                        Add contacts to receive alerts for all cameras on the site.
                      </Text>
                    </View>
                  </Card>
                </Animated.View>
              }

              {/* Add new contact */}
              <Animated.View layout={LinearTransition}>
                <PressableScale onPress={handleAddContact}>
                  <View style={styles.addContactRow}>
                    <View flex={1} style={[styles.addContactInner]}>
                      <Text textStyle="sm/regular" color="text">
                        Create new contact
                      </Text>
                      <Icon name="plus" size={20} color={theme.colors.text} />
                    </View>
                  </View>
                </PressableScale>
              </Animated.View>

              <Animated.View layout={LinearTransition}>
                <View>
                  <Text textStyle="xs/regular" color="textMuted">
                    These contacts receive alerts for all cameras on the site.
                  </Text>
                </View>
              </Animated.View>
              <Animated.View layout={LinearTransition}>
                <View h={1} bg="borderCard" my="4xl" />
              </Animated.View>
            </View>

            {/* Custom contacts */}
            <Animated.View layout={LinearTransition}>
              <View mb="lg">
                <Text textStyle="xs/regular" color="textMuted">
                  Custom contacts
                </Text>
              </View>

              <View>
                <Card style={styles.card}>
                  {cameraContactSummaries.map(({ camera, cameraKey, methods }) => (
                    <PressableScale key={cameraKey} onPress={() => handleCameraPress(cameraKey)}>
                      <View style={styles.cameraRow} px="5xl">
                        <View style={styles.cameraThumbnail}>
                          <LegacySnapshotImage
                            cameraKey={cameraKey}
                            thumbnailUrl={camera.snapshot?.url}
                            capturedAt={camera.snapshot?.capturedAt}
                            style={styles.thumbnailImage}
                          />
                        </View>
                        <View flex={1}>
                          <Text textStyle="sm/regular" color="text" numberOfLines={2}>
                            {cameraKey}
                          </Text>
                        </View>
                        <View flexDirection="row" alignItems="center" gap="sm">
                          <Text textStyle="xs/regular" color="textMuted">
                            {methods.join(", ")}
                          </Text>
                          <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
                        </View>
                      </View>
                    </PressableScale>
                  ))}
                </Card>
              </View>

              <View mt="lg">
                <Text textStyle="xs/regular" color="textMuted">
                  Set up specific contacts for specific cameras.
                </Text>
              </View>
            </Animated.View>
          </View>
        )}
      </ScrollView>
      <CreateSiteWideContactBottomSheet
        bottomSheetRef={createContactRef}
        onSave={handleSaveContact}
        isSaving={updateCameraContacts.isPending}
      />
      <ManageContactBottomSheet
        bottomSheetRef={manageContactRef}
        contact={selectedContact}
        onSave={handleManageContactSave}
        isSaving={updateCameraContacts.isPending}
      />
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["3xl"],
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  verifiedDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  addContactRow: {
    paddingVertical: theme.spacing["3xl"],
  },
  addContactInner: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing["3xl"],
    borderWidth: 1,
    borderRadius: theme.spacing.md,
    borderColor: theme.colors.interactive.buttons.default_background,
  },
  cameraRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderColor: theme.colors.borderCard,
    gap: theme.spacing["3xl"],
  },
  cameraThumbnail: {
    width: 58,
    height: 60,
    borderRadius: theme.spacing.xxs,
    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderRadius: theme.spacing.md,
  },
}));
